/** The order a respondent sees a question's options in — docs/adr/0009. Anchoring is position
 * only: it does NOT make an option exclusive. */
import { seededShuffle, stringToSeed } from './seededShuffle';

export type ChoiceAnchor = 'top' | 'bottom';

/** What `presentedChoices` needs off a question — structural, not the whole type. */
export interface ChoicePresentationQuestion {
  id?: string | number;
  choices?: string[];
  randomize_choices?: boolean;
  choice_anchors?: Record<string, ChoiceAnchor>;
  allow_other?: boolean;
}

const BOTTOM: ChoiceAnchor = 'bottom';
const TOP: ChoiceAnchor = 'top';

export function anchorsFor(question: ChoicePresentationQuestion): Record<string, ChoiceAnchor> {
  const choices = question.choices ?? [];
  const declared = question.choice_anchors ?? {};
  if (!question.allow_other || choices.length === 0) return declared;
  const last = choices[choices.length - 1];
  return last in declared ? declared : { ...declared, [last]: BOTTOM };
}

/** The options in the order this respondent should see them. `sessionKey` makes it stable. */
export function presentedChoices(
  question: ChoicePresentationQuestion | null | undefined,
  sessionKey: string | number | null | undefined,
): string[] {
  const authored = question?.choices ?? [];
  if (!question?.randomize_choices || authored.length <= 1) return authored;

  const anchors = anchorsFor(question);
  const top = authored.filter((c) => anchors[c] === TOP);
  const bottom = authored.filter((c) => anchors[c] === BOTTOM);
  const free = authored.filter((c) => !anchors[c]);

  const seed = stringToSeed(`${sessionKey ?? ''}:${question.id ?? ''}`);
  return [...top, ...seededShuffle(free, seed), ...bottom];
}

/** Empty is stored as absent: a `{}` map reads back as "this question has anchors". */
const orUndefined = (
  anchors: Record<string, ChoiceAnchor>,
): Record<string, ChoiceAnchor> | undefined =>
  Object.keys(anchors).length > 0 ? anchors : undefined;

/** The anchors map with `choice` pinned, or released when `anchor` is undefined. */
export function withAnchor(
  anchors: Record<string, ChoiceAnchor> | undefined,
  choice: string,
  anchor: ChoiceAnchor | undefined,
): Record<string, ChoiceAnchor> | undefined {
  const next = { ...(anchors ?? {}) };
  if (anchor) next[choice] = anchor;
  else delete next[choice];
  return orUndefined(next);
}

/**
 * Follow a renamed choice.
 *
 * Anchors are keyed by the choice's TEXT, so without this, anchoring "None of these" and then
 * fixing its spelling orphans the anchor and the option silently rejoins the shuffle — the
 * authored intent is lost with nothing on screen to say so.
 */
export function withRenamedChoice(
  anchors: Record<string, ChoiceAnchor> | undefined,
  from: string,
  to: string,
): Record<string, ChoiceAnchor> | undefined {
  if (!anchors || !(from in anchors) || from === to) return anchors;
  const next = { ...anchors };
  next[to] = next[from];
  delete next[from];
  return orUndefined(next);
}

export function shownChoiceOrderToSubmit(
  question: ChoicePresentationQuestion | null | undefined,
  sessionKey: string | number | null | undefined,
): string[] | undefined {
  if (!question?.randomize_choices) return undefined;
  const shown = presentedChoices(question, sessionKey);
  return shown.length > 1 ? shown : undefined;
}
